/**
 * The EmbedApiAdapter handed to the embedded Jasper designer workspace —
 * routes every designer call to TWASTA endpoints (the project's preview DB
 * and the pure-Python report engine) instead of the standalone jasper app's
 * backend. Connection ids from the embed are ignored: the adapter is bound
 * to one project.
 */
import type {
  EmbedApiAdapter, EmbedPreviewDataResult, EmbedQueryField,
  GenerateRequest, PreviewDataRequest, TableColumn, ValidateResponse,
} from '@jasper/embed'
import api from '@/api/client'
import { getTableInfo, getTables } from '@/api/designers'
import { runReportJob, validateReportJrxml } from '@/api/reportData'

interface DesignColumn {
  column_name?: string
  data_type?: string
  size?: string
  key?: boolean
  mandatory?: boolean
  description?: string
}

function toTableColumn(c: DesignColumn): TableColumn {
  const size = (c.size || '').trim()
  return {
    name: c.column_name || '',
    type: (c.data_type || 'VARCHAR') + (size ? `(${size})` : ''),
    nullable: !(c.mandatory || c.key),
    default: null,
    primary_key: !!c.key,
  }
}

export function makeReportEmbedApi(project: string): EmbedApiAdapter {
  const tableSchema = async (tableName: string): Promise<TableColumn[]> => {
    const res = await getTableInfo(project, tableName) as {
      table?: { columns?: DesignColumn[] }
    }
    return (res.table?.columns || []).map(toTableColumn)
  }

  return {
    async validate(jrxmlContent: string): Promise<ValidateResponse> {
      const res = await validateReportJrxml({ project, jrxml_content: jrxmlContent })
      return {
        success: res.success,
        valid: res.valid,
        errors: res.errors.map((e) => ({ ...e, severity: 'error' })),
        warnings: res.warnings.map((w) => ({ ...w, severity: 'warning' })),
        report_name: res.report_name || '',
        parameters: res.parameters.map((p) => ({
          name: p.name, class: p.java_class, description: '',
        })),
      }
    },

    async generate(req: GenerateRequest): Promise<Blob> {
      // Design-time "Generate": render the supplied JRXML directly (no saved
      // object). Values arriving from the embed's own parameter dialog are
      // already $P{}-name keyed — send them as jasper-level params via an
      // inline model that carries NO criteria (the embed dialog collected
      // the values), letting the engine substitute them.
      const files = req.jrxml_files
        ? Object.entries(req.jrxml_files).map(([filename, content]) => ({ filename, content }))
        : [{ filename: req.main_filename || 'report.jrxml', content: req.jrxml_content }]
      const mainFilename = req.main_filename || files[0]?.filename || 'report.jrxml'
      const inlineModel = {
        OBJ_TYPE: 'R',
        REPORT: {
          MAIN_JRXML: mainFilename,
          DEFAULT_OUTPUT_FORMAT: req.format || 'html',
          // Criteria empty: the embed prompted for parameter values itself.
          PARAMETERS: Object.keys(req.parameters || {}).map((name) => ({
            name, java_class: 'java.lang.String',
          })),
        },
        SQLMODEL: { CRITERIA: { query: { condition: 'AND', rules: [] } } },
      }
      // Through the render job, not /export: a designer Generate on real data
      // is as slow as a live run, and a long one used to die on the proxy's
      // read timeout rather than on anything the engine did.
      const out = await runReportJob({
        project,
        inline_model: inlineModel,
        jrxml_files: Object.fromEntries(files.map((f) => [f.filename, f.content])),
        // Raw jasper params — folded rules are empty, so pass directly:
        // the engine merges set_parameters over JRXML defaults.
        params: req.parameters || {},
        jasper_params: req.parameters || {},
        output_format: req.format || 'html',
      })
      if (!out.blob) {
        throw new Error(
          out.missingParams.length
            ? `The report needs values for: ${out.missingParams.join(', ')}`
            : 'The report produced no output.',
        )
      }
      return out.blob
    },

    async previewData(req: PreviewDataRequest): Promise<EmbedPreviewDataResult> {
      const res = await api.post('/report-data/query', {
        project,
        query: req.query,
        parameters: req.parameters,
        limit: req.limit ?? 50,
      })
      const d = res.data as {
        success: boolean; columns: string[]
        rows: Record<string, unknown>[]; total_rows: number
      }
      return { success: d.success, columns: d.columns, rows: d.rows, total_rows: d.total_rows }
    },

    async tables(): Promise<string[]> {
      const res = await getTables(project) as { tables?: Array<{ name: string }> }
      return (res.tables || []).map((t) => t.name)
    },

    tableSchema,

    async batchTableSchema(tables: string[]): Promise<Record<string, TableColumn[]>> {
      const out: Record<string, TableColumn[]> = {}
      await Promise.all(tables.map(async (t) => { out[t] = await tableSchema(t) }))
      return out
    },

    async foreignKeys() {
      // Database_Design carries no FK metadata today — the visual query
      // builder simply won't auto-suggest joins.
      return []
    },

    async queryFields(query: string): Promise<EmbedQueryField[]> {
      const res = await api.post('/report-data/query', { project, query, limit: 1 })
      const d = res.data as { fields?: EmbedQueryField[] }
      return d.fields || []
    },
  }
}
